import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Save, Eye, Send, Mail, Users, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { AnnouncementPreview, SendConfirm } from "./AnnouncementDialogs";

const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);

const STARTER = {
  title: "A small favour: help more musicians discover AMDS",
  body: "Hi {{first_name}},\n\nThanks for joining AMDS.\n\nI've been developing it to make managing musical commitments, finding colleagues and organising deps easier, while keeping everyone's diary private.\n\nNow I'm trying to get more musicians using it. The more colleagues who join, the more useful AMDS can become for everyone.\n\nIf you know a few musicians who might find it useful, I'd really appreciate you sharing your personal invitation link with them.\n\nYour personal invite link:\n{{invite_link}}\n\nIt's currently free for musicians during public beta.\n\nThanks for your support.\nDwight",
  audience: "musicians", send_in_app: true, send_email: true, include_invite: true,
};

export default function AnnouncementEditor({ initial, onClose }) {
  const [form, setForm] = useState(() => initial ? pick(initial) : STARTER);
  const [id, setId] = useState(initial?.id || null);
  const [dirty, setDirty] = useState(!initial);
  const [audience, setAudience] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [preview, setPreview] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const loadAudience = (aid) => api.get(`/admin/announcements/${aid}/audience`).then(({ data }) => setAudience(data)).catch(err);
  useEffect(() => { if (id) loadAudience(id); }, [id]);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = id ? await api.put(`/admin/announcements/${id}`, form) : await api.post("/admin/announcements", form);
      setId(data.id); setDirty(false); loadAudience(data.id);
      toast.success("Draft saved");
    } catch (e) { err(e); } finally { setBusy(false); }
  };

  const openPreview = async () => {
    try { const { data } = await api.post(`/admin/announcements/${id}/preview`); setPreview(data); } catch (e) { err(e); }
  };

  const testSend = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/announcements/${id}/test-send`, { to: testTo.trim() || null });
      toast.success(`Test email sent to ${data.to}`);
    } catch (e) { err(e); } finally { setBusy(false); }
  };

  const ready = id && !dirty;

  return (
    <div className="space-y-4 max-w-3xl" data-testid="announcement-editor">
      <button className="text-sm text-ink-600 inline-flex items-center gap-1 hover:text-ink-800" onClick={onClose} data-testid="announcement-back-btn">
        <ArrowLeft size={14} /> All announcements
      </button>

      <section className="card-base p-5 space-y-4">
        <label className="block">
          <span className="label-overline">Title / email subject</span>
          <input className="input-field mt-1" value={form.title} maxLength={140} onChange={(e) => set("title", e.target.value)} data-testid="announcement-title-input" />
        </label>
        <label className="block">
          <span className="label-overline">Message</span>
          <textarea rows={10} className="input-field mt-1" value={form.body} maxLength={5000} onChange={(e) => set("body", e.target.value)} data-testid="announcement-body-input" />
          <span className="text-xs text-ink-500 mt-1 block">
            Placeholders: <code>{"{{first_name}}"}</code> <code>{"{{name}}"}</code> <code>{"{{invite_link}}"}</code> <code>{"{{invite_code}}"}</code> — each person sees their own. "Hi {"{{first_name}}"}," becomes "Hello," when no name is set. A line with just {"{{invite_link}}"} becomes the invite box.
          </span>
        </label>

        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="label-overline">Send to</span>
            <select className="input-field mt-1" value={form.audience} onChange={(e) => set("audience", e.target.value)} data-testid="announcement-audience-select">
              <option value="musicians">Musicians</option>
              <option value="bookers">Bookers</option>
              <option value="all">All users</option>
            </select>
          </label>
          <div className="space-y-2 pt-1">
            <Check label="In-app (bell + Home banner)" checked={form.send_in_app} onChange={(v) => set("send_in_app", v)} testid="announcement-inapp-toggle" />
            <Check label="Email (verified accounts only)" checked={form.send_email} onChange={(v) => set("send_email", v)} testid="announcement-email-toggle" />
            <Check label="Add personal invite link box" checked={form.include_invite} onChange={(v) => set("include_invite", v)} testid="announcement-invite-toggle" />
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button className="btn-primary" onClick={save} disabled={busy || !dirty} data-testid="announcement-save-btn">
            <Save size={16} /> {id ? (dirty ? "Save changes" : "Saved") : "Save draft"}
          </button>
          <button className="btn-secondary" onClick={openPreview} disabled={!ready} data-testid="announcement-preview-btn"><Eye size={16} /> Preview</button>
        </div>
      </section>

      {id && <AudienceCard audience={audience} />}

      <section className={`card-base p-5 space-y-3 ${ready ? "" : "opacity-60 pointer-events-none"}`} data-testid="announcement-test-section">
        <div className="font-heading font-semibold flex items-center gap-2"><Mail size={16} className="text-sage-600" /> Test send</div>
        <p className="text-sm text-ink-600">Sends one email, filled in with your own name and invite link. Leave blank to send to yourself.</p>
        <div className="flex gap-2">
          <input className="input-field flex-1" type="email" placeholder="you@example.com" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="announcement-test-to-input" />
          <button className="btn-secondary" onClick={testSend} disabled={busy} data-testid="announcement-test-send-btn">Send test</button>
        </div>
      </section>

      <section className={`card-base p-5 flex flex-wrap items-center justify-between gap-3 ${ready && audience ? "" : "opacity-60 pointer-events-none"}`}>
        <div className="text-sm text-ink-600">When you're happy with the preview and the test email, send it to everyone selected.</div>
        <button className="btn-primary !bg-terracotta-500 hover:!bg-terracotta-600" onClick={() => setConfirming(true)} data-testid="announcement-send-btn">
          <Send size={16} /> Send announcement…
        </button>
      </section>

      {preview && <AnnouncementPreview data={preview} onClose={() => setPreview(null)} />}
      {confirming && audience && (
        <SendConfirm id={id} title={form.title} audience={audience} audienceKey={form.audience} onClose={() => setConfirming(false)} onSent={onClose} />
      )}
    </div>
  );
}

function AudienceCard({ audience }) {
  if (!audience) return <p className="text-sm text-ink-500">Counting recipients…</p>;
  const sk = audience.email_skipped;
  return (
    <section className="card-base p-5 space-y-2" data-testid="announcement-audience-card">
      <div className="font-heading font-semibold flex items-center gap-2"><Users size={16} className="text-sage-600" /> Recipients</div>
      <div className="grid grid-cols-3 gap-2">
        <Stat k="People in total" v={audience.total} testid="audience-total" />
        <Stat k="In-app" v={audience.in_app} testid="audience-inapp" />
        <Stat k="Email" v={audience.email} testid="audience-email" />
      </div>
      <div className="text-xs text-ink-500">
        Not emailed: {sk.not_verified} unverified · {sk.opted_out} unsubscribed. Suspended, deleted and demo accounts are always left out.
      </div>
      {audience.email_dry_run && (
        <div className="text-xs rounded-xl bg-amber-soft/40 border border-amber-soft p-2.5 flex gap-2 text-amber-deep" data-testid="announcement-dry-run-note">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          Preview environment: a full send records email deliveries as a dry run and sends no real emails. Test sends are real.
        </div>
      )}
    </section>
  );
}

function Stat({ k, v, testid }) {
  return (
    <div className="rounded-xl bg-ivory-100 p-3" data-testid={testid}>
      <div className="font-heading text-2xl font-bold">{v}</div>
      <div className="text-xs text-ink-600">{k}</div>
    </div>
  );
}

function Check({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 text-sm cursor-pointer">
      <input type="checkbox" className="accent-sage-500 w-4 h-4" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />
      {label}
    </label>
  );
}

function pick(a) {
  const { title, body, audience, send_in_app, send_email, include_invite } = a;
  return { title, body, audience, send_in_app, send_email, include_invite };
}
